// frontend/src/components/common/ContactSupportModal.jsx
//
// MUI dialog for contacting Manexys support from the SaaS dashboard.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DialogErrorBanner from './DialogErrorBanner';
import { useSupportMutation } from '../../hooks/useSupportMutation';
import { translateRequestError } from '../../queries/api';
import { lengthLimits } from '../../constants/orgFieldLimits';

const CATEGORIES = ['question', 'bug', 'billing', 'feature_request', 'other'];

// Mirrors support/serializers.py SupportRequestSerializer (see lengthLimits).
const SUPPORT_FIELD_MAX = { subject: 200, message: 5000 };

const EMPTY_FORM = {
  category: 'question',
  subject: '',
  message: '',
  sendCopy: true,
};

function ContactSupportModal({ open, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  // null until submitted; then { copySent } as reported by the server.
  // copySent is the server's answer, not the checkbox — the ack copy is
  // best-effort and can fail after the ticket itself landed. Telling the
  // merchant a copy was sent when it wasn't is a silent wrong answer.
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const mutation = useSupportMutation({
    onSuccess: (data) => {
      setResult({ copySent: data?.copy_sent === true });
      setErrorMessage(null);
    },
    onError: (err) => {
      console.error('ContactSupportModal:', err.message);
      setErrorMessage(translateRequestError(err, t, 'contact.sendFailed'));
    },
  });

  // Reset as the dialog starts to open rather than as it closes: the closing
  // frame keeps what the merchant was looking at, and every opening starts
  // fresh — including one that interrupts a close before it has finished.
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setResult(null);
    setErrorMessage(null);
    mutation.reset();
  };

  const handleSubmit = () => {
    setErrorMessage(null);
    mutation.mutate({
      category: form.category,
      subject: form.subject.trim(),
      message: form.message.trim(),
      sendCopy: form.sendCopy,
    });
  };

  const handleField = (field) => (e) => {
    const value = field === 'sendCopy' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const lengths = lengthLimits(form, SUPPORT_FIELD_MAX, t);

  const submitDisabled = mutation.isPending
    || form.subject.trim().length === 0
    || form.message.trim().length === 0
    || lengths.tooLong;

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      className="dialog-form"
      slotProps={{ transition: { onEnter: resetForm } }}
    >
      <DialogTitle>{t('contact.title')}</DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing={2}>
            <Typography variant="body1">
              {t('contact.successHeadline')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('contact.successBody')}
            </Typography>
            {result.copySent && (
              <Typography variant="body2" color="text.secondary">
                {t('contact.successCopyNote')}
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('contact.intro')}
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="contact-category-label">
                {t('contact.category')}
              </InputLabel>
              <Select
                labelId="contact-category-label"
                value={form.category}
                label={t('contact.category')}
                onChange={handleField('category')}
                disabled={mutation.isPending}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(`contact.categories.${c}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('contact.subject')}
              value={form.subject}
              onChange={handleField('subject')}
              fullWidth
              size="small"
              disabled={mutation.isPending}
              placeholder={t('contact.subjectPlaceholder')}
              {...lengths.propsFor('subject')}
            />

            <TextField
              label={t('contact.message')}
              value={form.message}
              onChange={handleField('message')}
              fullWidth
              multiline
              minRows={5}
              maxRows={10}
              disabled={mutation.isPending}
              placeholder={t('contact.messagePlaceholder')}
              {...lengths.propsFor('message')}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.sendCopy}
                  onChange={handleField('sendCopy')}
                  disabled={mutation.isPending}
                />
              }
              label={t('contact.sendCopy')}
            />
          </Stack>
        )}

        <DialogErrorBanner error={errorMessage} onClose={() => setErrorMessage(null)} />
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={onClose} variant="contained">
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitDisabled}
              loading={mutation.isPending}
            >
              {t('contact.send')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ContactSupportModal;
